import React, { useRef, useState } from 'react'
import { MdKeyboardDoubleArrowRight } from 'react-icons/md'
import { HiSparkles } from 'react-icons/hi2'
import { FaAngleRight } from 'react-icons/fa'

/**
 * Reusable horizontal-scrollable aircon service section.
 *
 * Props:
 *  - title          {string}   Section heading, e.g. "Chemical Wash"
 *  - jobScopeKey    {string}   Key passed to showJobScope(), e.g. "chemicalWash"
 *  - listings       {Array}    Filtered airconDeals items to display
 *  - quickBook      {Function} (unit, serviceType) => void
 *  - showJobScope   {Function} (key) => void
 *  - openRequestForm{Function} () => void
 *  - moreOptionsLabel{string}  Sub-label under "More Options" on desktop (optional)
 *
 * A listing may also carry its own `onBook`, `cardPrice`, `cardCurrency`,
 * `cardTitle`, `cardSubtitle` and `cardAriaLabel` (the UAE / Malaysia deals,
 * priced in AED / RM; the currency sits on its own small line above the price
 * pill, which "AED 1,188.64" would overflow on a phone). Singapore's airconDeals items have none and render as before -
 * the `card` prefix keeps clear of their own `title` / `description` fields.
 */
export const AirconScrollSection = ({
    title,
    jobScopeKey,
    listings,
    quickBook,
    showJobScope,
    openRequestModal,
    moreOptionsLabel,
}) => {
    const scrollRef = useRef(null)
    const [canScrollRight, setCanScrollRight] = useState(true)

    const scrollRight = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' })
        }
    }

    const handleScroll = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
            setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10)
        }
    }

    return (
        <section className='space-y-4 py-2'>
            <header className='space-y-2'>
                <div className='flex items-center space-x-2'>
                    <h3 className='font-bold sm:text-lg'>{title}</h3>
                    <button
                        type="button"
                        className="text-orange-600 text-sm flex items-center gap-1 hover:underline"
                        onClick={() => showJobScope(jobScopeKey)}
                        aria-label="View service details"
                    >
                        <MdKeyboardDoubleArrowRight className='text-xl sm:text-2xl animate-pulse' />
                        <span>Service Details&nbsp;</span>
                    </button>
                </div>
            </header>

            <div className='relative'>
                <div
                    ref={scrollRef}
                    className="flex sm:grid sm:grid-cols-3 md:grid-cols-5 gap-3 overflow-x-auto scrollbar-hide pr-12 sm:pr-0 pb-2"
                    onScroll={handleScroll}
                >
                    {listings.map((listing, i) => (
                        <button
                            type='button'
                            key={i}
                            className='relative overflow-hidden bg-white rounded-2xl shadow-lg border border-gray-100 hover:scale-102 hover:-translate-y-1 transition-all shrink-0 w-[calc(33%-8px)] sm:w-full'
                            onClick={() => (listing.onBook ? listing.onBook() : quickBook(listing.unit, listing.serviceType))}
                            role="link"
                            aria-label={listing.cardAriaLabel ?? `Book ${listing.unit} unit ${title.toLowerCase()}`}
                        >
                            {/* Photo removed: the deal cards carried decorative stock
                                images behind the price badge across 6 sections (~30
                                images), adding weight and height without information. */}
                            <div className="p-3 text-center">
                                {listing.cardCurrency && (
                                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">{listing.cardCurrency}</div>
                                )}
                                <div className="bg-linear-to-r from-indigo-500 to-purple-500 text-white text-sm sm:text-lg font-bold px-2 py-1 rounded-full shadow whitespace-nowrap inline-block">
                                    {/* toFixed: a cost like 412.90 is the number 412.9 */}
                                    {listing.cardPrice ?? <>${Number(listing.cost).toFixed(2)}</>}
                                </div>
                                <h4 className="font-semibold text-gray-800 text-sm mt-2">{listing.cardTitle ?? <>{listing.unit} AC</>}</h4>
                                <p className="text-xs text-gray-500">{listing.cardSubtitle ?? <>{listing.unitType} mount</>}</p>
                                {listing.originalCost > 0 && (
                                    <div className="text-xs text-gray-400 line-through">${listing.originalCost}</div>
                                )}
                            </div>
                        </button>
                    ))}

                    {/* Mobile "More" card — sits inside the scroll row */}
                    <button
                        type="button"
                        onClick={() => openRequestModal()}
                        className="md:hidden shrink-0 w-[calc(33%-8px)] rounded-2xl shadow-lg border border-gray-100 hover:scale-102 hover:-translate-y-1 transition-all bg-white overflow-hidden h-full"
                    >
                        <div className="p-3 text-center flex flex-col items-center justify-center h-full">
                            <HiSparkles className="text-2xl text-orange-500" />
                            <div className="font-bold text-gray-800 text-sm mt-2">More</div>
                            <div className="text-xs text-gray-500">Options</div>
                        </div>
                    </button>
                </div>

                {/* Scroll Indicator — Mobile Only */}
                {canScrollRight && (
                    <button
                        type="button"
                        onClick={scrollRight}
                        className="md:hidden animate-pulse absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 bg-white/95 backdrop-blur-sm rounded-full shadow-lg border border-gray-200"
                        aria-label="Scroll right"
                    >
                        <FaAngleRight className="text-purple-500 text-lg" />
                    </button>
                )}
            </div>

            {/* More Options Banner — Desktop Only */}
            <button
                type="button"
                onClick={() => openRequestModal()}
                className="hidden md:block w-full relative overflow-hidden bg-linear-to-br from-gray-700 to-gray-900 text-white rounded-2xl shadow-lg p-4"
            >
                <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -mr-10 -mt-10" />
                <div className="flex items-center justify-center gap-3">
                    <HiSparkles className="text-2xl text-gray-300" />
                    <div className="text-left">
                        <div className="font-semibold">More Options</div>
                        <div className="text-xs opacity-70">
                            {moreOptionsLabel ?? `Custom ${title.toLowerCase()}`}
                        </div>
                    </div>
                    <FaAngleRight className="text-gray-400" />
                </div>
            </button>
        </section>
    )
}
